import {
  FourWeekPlanActivity,
  ProgressEntry,
  useFinishFourWeekPlanFunctionRequest,
  useGetUserFourWeekPlanProgressFunctionRequest,
  useStartFourWeekPlanFunctionRequest,
  useSubmitFourWeekPlanActivitiesFunctionRequest,
} from "shared/request/myHealthyAdvantageApi";

import { DailyProgressActionButton } from "./DailyProgressActionButton";
import { DayNumber } from "./DayNumber";
import { Hideable } from "shared/UI/Hideable/Hideable";
import ReflectModal from "./modals/ReflectModal";
import { RoundedCard } from "shared/UI/RoundedCard/RoundedCard";
import { StartedOnDate } from "./StartedOnDate";
import { SubmitActivitiesModal } from "./modals/SubmitActivitiesModal";
import { TipsAndAdviceModal } from "./modals/TipsAndAdviceModal";
import { TrackerProgress } from "UIPalette/TrackerProgress/TrackerProgress";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { ViewStateWrapper } from "UIPalette/ViewStateWrapper/ViewStateWrapper";
import { trackFormSubmission } from "core/monitoring/Events";
import { useNotifyModalContext } from "shared/core/state/NotifyModalContext/NotifyModalContext";
import { useAppReviewContext } from "core/state/AppReviewContext/AppReviewContext";

export type DailyProgressProps = {
  fourWeekPlanId: string;
};
export const DailyProgress = ({ fourWeekPlanId }: DailyProgressProps) => {
  const { t } = useTranslation(undefined, { keyPrefix: "fourWeekPlans.dailyProgress" });
  const {
    data: progressData,
    isLoading,
    error,
    mutate: refreshDailyProgress,
  } = useGetUserFourWeekPlanProgressFunctionRequest({ id: fourWeekPlanId });
  const { trigger: startFourWeekPlanAPI } = useStartFourWeekPlanFunctionRequest({});
  const { trigger: submitActivitiesAPI } = useSubmitFourWeekPlanActivitiesFunctionRequest({});
  const { trigger: finishFourWeekPlanAPI } = useFinishFourWeekPlanFunctionRequest({});
  const { handlePositiveMoment } = useAppReviewContext();
  const [showTips, setShowTips] = useState(false);
  const [showSubmitActivities, setShowSubmitActivities] = useState(false);
  const [showReflectModal, setShowReflectModal] = useState(false);
  const { modalNotify } = useNotifyModalContext();

  const { successNotes, challengeNotes } = progressData ?? {};

  const progressIndicators = [] as Array<{ day: number; percentForDay: number }>;
  for (let day = 1; day < 29; day++) {
    const percentForDay = progressData?.entries?.find((x: ProgressEntry) => x.day === day)?.percentageComplete ?? 0;
    progressIndicators.push({ day, percentForDay });
  }

  async function tipsOnConfirm() {
    try {
      await startFourWeekPlanAPI({ body: { fourWeekPlanDatoId: fourWeekPlanId } });
      setShowTips(false);
      setShowSubmitActivities(true);
    } catch {
      modalNotify("error", t("startPlanErrorTitle"), t("startPlanError"), t("errorCta"));
    }
  }

  async function onSubmitActivities(activities: FourWeekPlanActivity[]) {
    const data = {
      activities: activities,
      datoFourWeekPlanId: fourWeekPlanId,
    };
    trackFormSubmission("Four-Week-Plan-Activity", data, false);
    try {
      await submitActivitiesAPI({
        body: data,
      });
      await refreshDailyProgress();
      setShowSubmitActivities(false);
      if (activities.every((activity) => activity.isCompleted)) {
        handlePositiveMoment();
      }
    } catch {
      modalNotify("error", t("submitErrorTitle"), t("submitError"), t("errorCta"));
    }
  }

  async function onFinishPlan(successNotes: string, challengeNotes: string) {
    const { userFourWeekPlanId } = progressData!;
    if (!userFourWeekPlanId) {
      return;
    }

    try {
      await finishFourWeekPlanAPI({
        body: {
          userFourWeekPlanId,
          successNotes,
          challengeNotes,
        },
      });
      setShowReflectModal(false);
      await refreshDailyProgress();
      handlePositiveMoment();
    } catch {
      modalNotify("error", t("finishErrorTitle"), t("finishError"), t("errorCta"));
    }
  }

  return (
    <>
      <div className="mt-2 mb-2">
        <RoundedCard>
          <h2>{t("title")}</h2>
          <ViewStateWrapper loading={isLoading} error={!!error} errorMessage={t("error")} onRetry={refreshDailyProgress}>
            <div className="flex flex-col gap-4 mt-4 mb-6">
              <StartedOnDate startDate={progressData?.startDate} />
              <div className="flex flex-wrap">
                {progressIndicators.map(({ day, percentForDay }) => (
                  <div key={day} className="sm:basis-1/7 basis-1/4">
                    <TrackerProgress percentage={percentForDay}>
                      <DayNumber day={day} />
                    </TrackerProgress>
                  </div>
                ))}
              </div>
            </div>
            <DailyProgressActionButton
              startDate={progressData?.startDate}
              planOnClicks={{
                start: () => setShowTips(true),
                mid: () => setShowSubmitActivities(true),
                end: () => setShowReflectModal(true),
              }}
              hasNotes={!!successNotes ?? !!challengeNotes}
            />
          </ViewStateWrapper>
        </RoundedCard>

        <Hideable hidden={!showTips}>
          <TipsAndAdviceModal datoFourWeekPlanId={fourWeekPlanId} onCancel={() => setShowTips(false)} onConfirm={tipsOnConfirm} />
        </Hideable>

        <Hideable hidden={!showSubmitActivities}>
          <SubmitActivitiesModal
            datoFourWeekPlanId={fourWeekPlanId}
            onCancel={() => {
              setShowSubmitActivities(false);
            }}
            onConfirm={onSubmitActivities}
            startDate={progressData?.startDate}
          />
        </Hideable>

        <Hideable hidden={!showReflectModal}>
          <ReflectModal
            onCancel={() => setShowReflectModal(false)}
            onConfirm={onFinishPlan}
            notes={{
              successNotes,
              challengeNotes,
            }}
          />
        </Hideable>
      </div>
    </>
  );
};
